// LoadFilter.tsx
import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { useToastStore } from 'store/toastStore';

interface LoadFilterProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: { 
    fromDate?: string; 
    toDate?: string;
  }) => void;
  onClear: () => void;
}

const LoadFilter: React.FC<LoadFilterProps> = ({ isOpen, onClose, onApply, onClear }) => {
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);
  const { showToast } = useToastStore();

  // Helper function to format date in local timezone as YYYY-MM-DD
  const formatDateLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to check if two dates are the same (ignoring time)
  const areDatesSame = (date1: Date | null, date2: Date | null): boolean => {
    if (!date1 || !date2) return false;
    return formatDateLocal(date1) === formatDateLocal(date2);
  };

  const handleApply = () => {
    // Validate that dates are not the same
    if (fromDate && toDate && areDatesSame(fromDate, toDate)) {
      const errorMessage = "From Date and To Date cannot be the same. Please select different dates.";
      setDateError(errorMessage);
      showToast(errorMessage, "error", 3000);
      return;
    }

    setDateError(null);
    onApply({
      fromDate: fromDate ? formatDateLocal(fromDate) : undefined,
      toDate: toDate ? formatDateLocal(toDate) : undefined,
    });
    onClose();
  };

  const handleFromDateChange = (date: Date | null) => {
    setFromDate(date);
    // Clear error when user changes the date
    if (dateError) {
      setDateError(null);
    }
  };

  const handleToDateChange = (date: Date | null) => {
    setToDate(date);
    // Clear error when user changes the date
    if (dateError) {
      setDateError(null);
    }
  };

  const handleClear = () => {
    setFromDate(null);
    setToDate(null);
    setDateError(null);
    onClear();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="absolute right-0 z-10 bg-white rounded-lg shadow w-96 mt-2">
      <div className="flex items-center justify-between p-3 border-b border-lightgray-100">
        <h3 className="text-base font-semibold text-grease-100">Filters</h3>
        <button onClick={onClose}>
          <svg className="w-3 h-3" viewBox="0 0 14 14">
            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
          </svg>
        </button>
      </div>
      <div className="bg-indigo-200 bg-opacity-25 rounded-lg mt-4 p-4 mx-3 mb-4">
        <p className="text-sm">Date Range</p>
        <div className="flex sm:flex-nowrap flex-wrap items-center gap-4 mt-3">
          <DatePicker
            selected={fromDate}
            onChange={handleFromDateChange}
            selectsStart
            startDate={fromDate}
            endDate={toDate}
            placeholderText="From Date"
            className={`p-3 rounded-lg bg-white text-xs w-full ${dateError ? 'border border-red-500' : ''}`}
            dateFormat="yyyy-MM-dd"
          />
          <DatePicker
            selected={toDate}
            onChange={handleToDateChange}
            selectsEnd
            startDate={fromDate}
            endDate={toDate}
            minDate={fromDate || undefined}
            placeholderText="To Date"
            className={`p-3 rounded-lg bg-white text-xs w-full ${dateError ? 'border border-red-500' : ''}`}
            dateFormat="yyyy-MM-dd"
          />
        </div>
        {dateError && (
          <p className="text-red-500 text-xs mt-2 ml-1">{dateError}</p>
        )}
      </div>
      <div className="flex justify-between p-3">
        <button className="px-3 py-2 bg-blue-500 bg-opacity-25 rounded-md" onClick={onClose}>
          Cancel
        </button>
        <div className="flex gap-4">
          <button className="px-3 py-2 border border-royalblue-100 rounded-md" onClick={handleClear}>
            Clear All
          </button>
          <button className="px-3 py-2 bg-royalblue-100 rounded-md text-white" onClick={handleApply}>
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoadFilter;

